/**
 * compare-popup.js
 * Reads from localStorage key "compareProducts" (written by global.js)
 * Does NOT handle clicks — global.js owns all add/remove logic.
 * This file only renders the bar + popup and keeps them in sync.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'compareProducts';
  const MAX_COMPARE = 3;
  const BAR_ID      = 'cp-bar';
  const POPUP_ID    = 'cp-popup';

  // Tracks the last list we actually rendered, so the polling loop below
  // can skip rebuilding the bar's innerHTML (and re-creating its <img>
  // tags) when nothing has changed — that repeated rebuild every 300ms
  // was what caused the product image to visibly flicker on mobile.
  let lastRenderedSnapshot = null;

  /* ── Storage ─────────────────────────────────────────────────────── */

  function getList() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  }

  function removeProduct(title) {
    const updated = getList().filter(p => p.title !== title);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    refresh();
  }

  function clearList() {
    localStorage.removeItem(STORAGE_KEY);
    refresh();
  }

  /* ── Bar ─────────────────────────────────────────────────────────── */

  function renderBar() {
    const list = getList();
    let bar = document.getElementById(BAR_ID);

    if (!list.length) {
      if (bar) bar.hidden = true;
      lastRenderedSnapshot = '[]';
      return;
    }

    if (!bar) {
      bar = document.createElement('div');
      bar.id = BAR_ID;
      document.body.appendChild(bar);
    }

    bar.hidden = false;
    bar.innerHTML = `
      <div class="cpbar__items">
        ${list.map(p => `
          <div class="cpbar__item">
            <img src="${p.image || ''}" alt="${p.title}" loading="lazy">
            <span class="cpbar__name">${p.title}</span>
            <button class="cpbar__remove" data-title="${p.title}" aria-label="Remove ${p.title}">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>
        `).join('')}
        ${Array.from({ length: MAX_COMPARE - list.length }, (_, i) => `
          <div class="cpbar__slot">Empty slot ${list.length + i + 1}</div>
        `).join('')}
      </div>
      <div class="cpbar__actions">
        <button class="cpbar__btn-compare" ${list.length < 2 ? 'disabled' : ''}>
          Compare (${list.length})
        </button>
        <button class="cpbar__btn-clear">Clear all</button>
      </div>
    `;

    bar.querySelectorAll('.cpbar__remove').forEach(btn => {
      btn.addEventListener('click', () => removeProduct(btn.dataset.title));
    });
    bar.querySelector('.cpbar__btn-compare').addEventListener('click', openPopup);
    bar.querySelector('.cpbar__btn-clear').addEventListener('click', clearList);
  }

  /* ── Popup ───────────────────────────────────────────────────────── */

  function row(label, cells) {
    return `<tr><th scope="row" style="position:sticky;left:0;z-index:2;background:#fff;">${label}</th>${cells.map(v => `<td>${v || '—'}</td>`).join('')}</tr>`;
  }

  function badge(availability) {
    const inStock = /in.?stock/i.test(availability || '');
    return `<span class="cppop__badge ${inStock ? 'cppop__badge--in' : 'cppop__badge--out'}">${availability || '—'}</span>`;
  }

  function openPopup() {
    const list = getList();
    if (list.length < 2) return;

    let popup = document.getElementById(POPUP_ID);
    if (!popup) {
      popup = document.createElement('div');
      popup.id = POPUP_ID;
      popup.setAttribute('role', 'dialog');
      popup.setAttribute('aria-modal', 'true');
      document.body.appendChild(popup);
    }

    popup.hidden = false;
    popup.innerHTML = `
      <div class="cppop__backdrop"></div>
      <div class="cppop__panel">
        <div class="cppop__header">
          <h2 class="cppop__title">Compare Products</h2>
          <button class="cppop__close" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="cppop__scroll">
          <table class="cppop__table">
            <thead>
              <tr>
                <th style="position:sticky;left:0;z-index:3;background:#fff;">&nbsp;</th>
                ${list.map(p => `
                  <th>
                    <a href="${p.url || '#'}" class="cppop__product-link">
                      <img src="${p.image || ''}" alt="${p.title}" style="position:static;top:auto;left:auto;right:auto;bottom:auto;transform:none;float:none;margin:0 auto;display:block;">
                      <span>${p.title}</span>
                    </a>
                  </th>
                `).join('')}
              </tr>
            </thead>
            <tbody>
              ${row('Price',        list.map(p => `<strong>${p.price || ''}</strong>`))}
              ${row('Availability', list.map(p => badge(p.availability)))}
              ${row('Type',         list.map(p => p.type))}
              ${row('Vendor',       list.map(p => p.vendor))}
              ${row('Description',  list.map(p => `<span class="cppop__desc">${p.description || ''}</span>`))}
              ${row('', list.map(p => `<a class="cppop__view-btn" href="${p.url || '#'}">View product</a>`))}
            </tbody>
          </table>
        </div>
      </div>
    `;

    popup.querySelector('.cppop__close').addEventListener('click', closePopup);
    popup.querySelector('.cppop__backdrop').addEventListener('click', closePopup);
    popup.addEventListener('keydown', e => { if (e.key === 'Escape') closePopup(); });
    popup.querySelector('.cppop__close').focus();

    // Force the horizontally-scrollable table to start showing the first
    // product from the left edge. Without this, some mobile browsers can
    // leave the scroll container at a non-zero scrollLeft (or it inherits
    // an rtl direction from an ancestor), which makes the first product
    // appear pushed off-screen to the right — looking like the products
    // were reordered when they weren't.
    const scrollEl = popup.querySelector('.cppop__scroll');
    if (scrollEl) {
      scrollEl.style.direction = 'ltr';
      scrollEl.scrollLeft = 0;
      // Some browsers apply the scroll reset a frame late once content
      // has actually laid out — set it again after paint to be safe.
      requestAnimationFrame(() => { scrollEl.scrollLeft = 0; });
    }
  }

  function closePopup() {
    const popup = document.getElementById(POPUP_ID);
    if (popup) popup.hidden = true;
  }

  /* ── Sync button states ──────────────────────────────────────────── */

  function syncButtons() {
    const titles = getList().map(p => p.title);
    document.querySelectorAll('.compare_button').forEach(btn => {
      const isAdded = titles.includes(btn.dataset.title);
      btn.classList.toggle('active', isAdded);
      btn.title = isAdded ? 'Remove from Compare' : 'Compare';
    });
  }

  /* ── Main refresh ────────────────────────────────────────────────── */

  function refresh() {
    renderBar();
    syncButtons();
    lastRenderedSnapshot = JSON.stringify(getList());
  }

  /* ── Watch localStorage for changes made by global.js ───────────── */

  // Poll every 300ms, but only actually re-render (and thus recreate the
  // <img> elements) when the stored list has changed since the last
  // render. Previously this rebuilt the bar's innerHTML unconditionally
  // on every tick, which reloaded every product image ~3x per second and
  // showed up as a visible flicker on mobile.
  setInterval(() => {
    const currentSnapshot = JSON.stringify(getList());
    if (currentSnapshot !== lastRenderedSnapshot) {
      refresh();
    }
  }, 300);

  // Also sync on storage event (cross-tab)
  window.addEventListener('storage', e => {
    if (e.key === STORAGE_KEY) refresh();
  });

  /* ── Init ────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  window.CompareBar = { open: openPopup, close: closePopup, refresh };

})();